import { inject, Injectable, NgModule } from '@angular/core';
import {
  RouterModule,
  RouterStateSnapshot,
  Routes,
  TitleStrategy,
  UrlSerializer,
} from '@angular/router';
import {
  canMatchAdminRole,
  canMatchFeatureFlag,
  canMatchInternalUser,
} from './guards/permissions';
import { canMatchProfileComplete } from './guards/profileComplete';
import { OktaAuthGuard } from './guards/okta-auth';
import { MaintenanceGuard } from './guards/maintenance.guard';
import { Role } from '@shared/interfaces/permissions.interface';
import { Title } from '@angular/platform-browser';
import { FeatureFlag } from '@data-access/system-controls/feature-flags';
import {
  SubmissionsListService,
  SUBMISSSION_DETAILS_V2_PATH,
} from '@data-access/submissions';
import { WcgTableService } from '@wcg/frontend-framework';
import { SUBMISSION_LIST_PROVIDER } from '@data-access/submissions/store/tokens/submission-list.token';
import {
  OrganizationsListSignalState,
  OrganizationsV2Service,
} from '@data-access/organizations';
import { permissionProviders } from '@data-access/shared';
import {
  ADD_USER_ORGANIZATION_PERMISSIONS,
  ORGANIZATION_ADMIN_PERMISSIONS,
  REMOVE_USER_ORGANIZATION_PERMISSIONS,
  VIEW_ORGANIZATION_USER_PERMISSIONS,
  VIEW_ORGANIZATION_PERMISSIONS,
} from '@data-access/permissions';
import { AuthService } from '@auth/auth.service';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: '/welcome',
  },
  {
    loadComponent: () =>
      import('./maintenance/maintenance.component').then(
        (m) => m.MaintenanceComponent,
      ),
    path: 'maintenance',
    title: 'System Maintenance',
  },
  {
    loadComponent: () =>
      import('./auth-error/auth-error.component').then(
        (m) => m.AuthErrorComponent,
      ),
    path: 'erm-auth-403',
    title: 'Sign In Error',
  },
  {
    canMatch: [OktaAuthGuard, MaintenanceGuard],
    data: { type: 'home' },
    // loadComponent: () =>
    //   import('./home/components/home/').then((m) => m.HomeComponent),
    loadChildren: () =>
      import('./home/components/home').then((m) => m.HOME_ROUTES),
    path: 'welcome',
    providers: [
      SUBMISSION_LIST_PROVIDER,
      WcgTableService,
      SubmissionsListService,
      AuthService,
    ],
    title: 'Welcome',
  },
  {
    canMatch: [OktaAuthGuard, MaintenanceGuard],
    loadComponent: () =>
      import('./server-down/server-down.component').then(
        (m) => m.ServerDownComponent,
      ),
    path: 'error',
    title: 'Whoops',
  },
  {
    loadChildren: () => import('./auth/auth.module').then((m) => m.AuthModule),
    path: 'auth',
  },
  {
    canMatch: [OktaAuthGuard, MaintenanceGuard],
    loadChildren: () =>
      import('@submissions/v1/submissions.module').then(
        (m) => m.SubmissionsV1Module,
      ),
    path: 'submissions',
    title: 'Submissions',
  },
  {
    canMatch: [OktaAuthGuard, MaintenanceGuard],
    loadChildren: () =>
      import('./submissions/next/submissions.module').then(
        (m) => m.SubmissionsNextModule,
      ),
    path: SUBMISSSION_DETAILS_V2_PATH,
    title: 'Submissions',
  },
  {
    canMatch: [OktaAuthGuard, MaintenanceGuard, canMatchProfileComplete],
    loadChildren: () =>
      import('./study/iris-study.module').then((m) => m.IrisStudyModule),
    path: 'studies',
    title: 'Studies',
  },
  {
    path: 'sites',
    loadChildren: () =>
      import('./iris-sites/erm-sites.module').then((m) => m.ERMSitesModule),
    canMatch: [OktaAuthGuard, MaintenanceGuard, canMatchProfileComplete],
    title: 'Sites',
  },
  {
    canMatch: [OktaAuthGuard, MaintenanceGuard, canMatchInternalUser],
    loadChildren: () => import('./profiles').then((m) => m.PROFILE_ROUTES),
    path: 'profiles',
    title: 'Profiles',
  },
  {
    canMatch: [OktaAuthGuard, MaintenanceGuard],
    loadChildren: () => import('./organizations').then((m) => m.ORG_ROUTES),
    path: 'organizations',
    title: 'Organizations',
    providers: [
      OrganizationsListSignalState,
      OrganizationsV2Service,
      ...permissionProviders({
        ...ORGANIZATION_ADMIN_PERMISSIONS(),
      }),
      ...permissionProviders({
        ...VIEW_ORGANIZATION_PERMISSIONS(),
      }),
      ...permissionProviders({
        ...ADD_USER_ORGANIZATION_PERMISSIONS(),
      }),
      ...permissionProviders({
        ...REMOVE_USER_ORGANIZATION_PERMISSIONS(),
      }),
      ...permissionProviders({
        ...VIEW_ORGANIZATION_USER_PERMISSIONS(),
      }),
    ],
  },
  {
    path: 'admin',
    loadChildren: () =>
      import('./admin/admin.module').then((m) => m.AdminModule),
    canMatch: [OktaAuthGuard, MaintenanceGuard, canMatchInternalUser],
    title: 'Admin',
  },
  {
    path: 'user',
    loadChildren: () => import('./user/user.module').then((m) => m.UserModule),
    canMatch: [OktaAuthGuard, MaintenanceGuard, canMatchProfileComplete],
    title: 'People',
  },
  {
    path: 'workspaces',
    loadChildren: () =>
      import('./setup/workspace/erm-workspace.module').then(
        (m) => m.ERMWorkspaceModule,
      ),
    canMatch: [OktaAuthGuard, MaintenanceGuard, canMatchProfileComplete],
    title: 'Submission',
  },
  {
    path: 'submission-setup',
    loadChildren: () =>
      import('./submission-setup/submission-setup.module').then(
        (m) => m.SubmissionSetupModule,
      ),
    canMatch: [OktaAuthGuard, MaintenanceGuard, canMatchProfileComplete],
    title: 'Submission Setup',
  },
  {
    path: 'audit',
    loadChildren: () =>
      import('./audit/audit-routing.module').then((m) => m.AuditRoutingModule),
    canMatch: [
      OktaAuthGuard,
      MaintenanceGuard,
      canMatchInternalUser,
      canMatchProfileComplete,
    ],
  },
  {
    path: 'mock-iris',
    loadChildren: () =>
      import('./mock-iris/mock-iris-routing.module').then(
        (m) => m.MockIrisRoutingModule,
      ),
    canMatch: [
      OktaAuthGuard,
      MaintenanceGuard,
      canMatchAdminRole(Role.ITAdmin),
      canMatchFeatureFlag(FeatureFlag.ENABLE_MOCK_IRIS),
    ],
  },
  {
    path: '**',
    redirectTo: '/welcome',
  },
];

@Injectable({ providedIn: 'root' })
export class eRMTitleStrategy extends TitleStrategy {
  public title = inject(Title);
  constructor() {
    super();
  }

  override updateTitle(routerState: RouterStateSnapshot): void {
    const title = this.buildTitle(routerState);
    if (title) {
      this.title.setTitle(`eReview Manager - ${title}`);
    } else {
      this.title.setTitle('eReview Manager');
    }
  }
}

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      anchorScrolling: 'enabled',
      canceledNavigationResolution: 'replace',
      onSameUrlNavigation: 'reload',
      paramsInheritanceStrategy: 'always',
      scrollPositionRestoration: 'top',
      urlUpdateStrategy: 'deferred',
    }),
  ],
  providers: [
    {
      provide: 'malformedUriErrorHandler',
      useValue: (_: URIError, urlSerializer: UrlSerializer, __: string) =>
        urlSerializer.parse('/welcome'),
    },
    {
      provide: TitleStrategy,
      useClass: eRMTitleStrategy,
    },
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
